import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChevronRight, Bed, Utensils, Route as RouteIcon, Camera,
  Navigation, Play, ExternalLink, ArrowRight, MapPin,
} from "lucide-react";
import { fetchCity, fetchRoutes } from "@/lib/api";
import RouteCard from "@/components/rotta/RouteCard";
import { Reveal } from "@/lib/motion";
import { splitParagraphs, mapsSearchUrl, mapsLocationUrl } from "@/lib/text";
import { toUF } from "@/lib/brStates";

function ytId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:.*v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function CidadeDetail() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [allRoutes, setAllRoutes] = useState([]);

  useEffect(() => {
    setLoading(true);
    fetchCity(slug).then((c) => { setCity(c); setLoading(false); }).catch(() => setLoading(false));
    fetchRoutes().then(setAllRoutes).catch(() => {});
  }, [slug]);

  const nearbyRoutes = useMemo(() => {
    if (!city || !allRoutes.length) return [];
    const cityUf = toUF(city.state);
    const cityName = (city.name || "").toLowerCase();

    const scored = allRoutes.map((r) => {
      let score = 0;
      const rUf = toUF(r.state);
      if (city.related_route_slugs?.includes(r.slug)) score += 100;
      if (rUf && cityUf && rUf === cityUf) score += 30;
      const hay = `${r.name || ""} ${r.region || ""} ${r.short_description || ""}`.toLowerCase();
      if (cityName && hay.includes(cityName)) score += 20;
      if (r.verified) score += 5;
      return { r, score };
    });

    return scored
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((x) => x.r);
  }, [city, allRoutes]);

  if (loading) return <div className="pt-40 text-center text-[#B8B8B8]">Carregando…</div>;
  if (!city) return <div className="pt-40 text-center text-[#B8B8B8]">Cidade não encontrada.</div>;

  const yt = ytId(city.video_url);
  const hasCoords = typeof city.latitude === "number" && typeof city.longitude === "number";
  const mapsQuery = hasCoords ? `${city.latitude},${city.longitude}` : encodeURIComponent(`${city.name}, ${city.state}, Brasil`);
  const mapEmbed = `https://maps.google.com/maps?q=${mapsQuery}&z=12&output=embed`;
  const mapsOpenUrl = city.google_maps_url || mapsLocationUrl({
    lat: city.latitude, lng: city.longitude,
    query: `${city.name}, ${city.state}, Brasil`,
  });

  const paragraphs = splitParagraphs(city.description);
  const howToGetParagraphs = splitParagraphs(city.how_to_get);

  const cityContext = `${city.name}, ${city.state}, Brasil`;
  const gallery = city.gallery || [];

  return (
    <div data-testid={`city-detail-${city.slug}`}>
      {/* HERO */}
      <section className="relative h-[70vh] min-h-[520px] w-full overflow-hidden flex items-end">
        {city.cover_image && (
          <img src={city.cover_image} alt={city.name} className="absolute inset-0 w-full h-full object-cover slow-zoom" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/30 via-[#0B0B0B]/40 to-[#0B0B0B]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B]/85 via-transparent to-transparent" />
        <div className="container-rotta relative z-10 pb-16 md:pb-20">
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/70 mb-4 flex items-center gap-2">
            <Link to="/cidades" className="hover:text-[#C6A762]">Cidades</Link>
            <ChevronRight size={12} /><span className="text-[#C6A762]">{city.state}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.95]">{city.name}</h1>
          <p className="text-white/85 mt-5 max-w-2xl text-lg">{city.short_description}</p>
        </div>
      </section>

      {/* SOBRE — respeita parágrafos */}
      {paragraphs.length > 0 && (
        <section className="section" data-testid="city-about">
          <div className="container-rotta max-w-3xl">
            <Reveal>
              <div className="flex items-center gap-3 text-[#C6A762] mb-5">
                <span className="gold-divider" />
                <span className="text-[11px] tracking-[0.35em] uppercase">Sobre</span>
              </div>
              <div className="blog-content" data-testid="city-description">
                {paragraphs.map((p, i) => (
                  <p key={i} className="text-[#D6D6D6] leading-relaxed text-lg mb-6 last:mb-0">{p}</p>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* INFO — Onde ficar / Onde comer / O que visitar / Melhores estradas */}
      <section className={`section ${paragraphs.length > 0 ? "pt-0" : ""}`} data-testid="city-info">
        <div className="container-rotta grid md:grid-cols-2 gap-6">
          <InfoBlock icon={<Bed size={16} />} title="Onde ficar" items={city.where_to_stay} cityContext={cityContext} showMap />
          <InfoBlock icon={<Utensils size={16} />} title="Onde comer" items={city.where_to_eat} cityContext={cityContext} showMap />
          <InfoBlock icon={<Camera size={16} />} title="O que visitar" items={city.what_to_visit?.length ? city.what_to_visit : city.points_of_interest} cityContext={cityContext} showMap />
          <InfoBlock icon={<RouteIcon size={16} />} title="Melhores estradas" items={city.best_roads} />
        </div>

        {howToGetParagraphs.length > 0 && (
          <div className="container-rotta mt-6">
            <div className="card-rotta p-7 bg-[#141414]">
              <div className="flex items-center gap-3 text-[#C6A762] mb-4">
                <Navigation size={16} />
                <span className="text-[11px] tracking-[0.3em] uppercase">Como chegar de moto</span>
              </div>
              <div className="blog-content">
                {howToGetParagraphs.map((p, i) => (
                  <p key={i} className="text-[#D6D6D6] leading-relaxed mb-4 last:mb-0">{p}</p>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Video */}
      {yt && (
        <section className="section pt-0" data-testid="city-video">
          <div className="container-rotta">
            <div className="flex items-center gap-3 text-[#C6A762] mb-6">
              <span className="gold-divider" />
              <span className="text-[11px] tracking-[0.35em] uppercase">Vídeo</span>
            </div>
            <div className="card-rotta overflow-hidden relative aspect-video group">
              <img src={`https://i.ytimg.com/vi/${yt}/maxresdefault.jpg`} alt={city.name} className="w-full h-full object-cover opacity-90" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B]/80 to-transparent" />
              <a href={`https://www.youtube.com/watch?v=${yt}`} target="_blank" rel="noreferrer" className="absolute inset-0 grid place-items-center">
                <span className="btn-gold px-7 py-4 rounded-full text-sm inline-flex items-center gap-3 group-hover:scale-105 transition-transform">
                  <Play size={16} fill="#0B0B0B" /> Assistir no YouTube
                </span>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Localização + CTA Abrir no Google Maps */}
      <section className="section pt-0" data-testid="city-map">
        <div className="container-rotta">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3 text-[#C6A762] mb-2">
                <span className="gold-divider" />
                <span className="text-[11px] tracking-[0.35em] uppercase">Localização</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl text-white leading-[1.05]">{city.name} no mapa.</h2>
            </div>
            {mapsOpenUrl && (
              <a
                href={mapsOpenUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="city-open-maps"
                className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2"
              >
                Abrir no Google Maps <ExternalLink size={14} />
              </a>
            )}
          </div>
          <div className="card-rotta overflow-hidden">
            <iframe src={mapEmbed} title={`Mapa ${city.name}`} className="w-full h-[440px]" loading="lazy" />
          </div>
        </div>
      </section>

      {/* GALERIA EDITORIAL — 1 principal + secundárias em grid */}
      {gallery.length > 0 && (
        <section className="section pt-0" data-testid="city-gallery">
          <div className="container-rotta">
            <div className="flex items-center gap-3 text-[#C6A762] mb-6">
              <span className="gold-divider" />
              <span className="text-[11px] tracking-[0.35em] uppercase">Galeria</span>
            </div>
            <EditorialGallery images={gallery} onOpen={setLightbox} />
          </div>
        </section>
      )}

      {/* ROTAS PRÓXIMAS — máx 3, curadoria */}
      {nearbyRoutes.length > 0 && (
        <section className="section pt-0" data-testid="city-nearby-routes">
          <div className="container-rotta">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <div className="flex items-center gap-3 text-[#C6A762] mb-3">
                  <span className="gold-divider" />
                  <span className="text-[11px] tracking-[0.35em] uppercase">Continue a estrada</span>
                </div>
                <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.05]">
                  Rotas próximas de <span className="text-gold-gradient">{city.name}</span>.
                </h2>
              </div>
              <Link
                to="/rotas"
                data-testid="city-see-all-routes"
                className="text-[#C6A762] text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2 hover:translate-x-1 transition-transform"
              >
                Ver todas as rotas <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid gap-7 md:grid-cols-3">
              {nearbyRoutes.map((r) => <RouteCard key={r.slug} route={r} />)}
            </div>
          </div>
        </section>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)} className="fixed inset-0 z-[100] bg-[#0B0B0B]/95 backdrop-blur grid place-items-center p-6 cursor-zoom-out">
          <img src={lightbox} alt="" className="max-h-[90vh] max-w-[90vw] rounded-xl" />
        </div>
      )}
    </div>
  );
}

function InfoBlock({ icon, title, items = [], cityContext = "", showMap = false }) {
  if (!items?.length) return null;
  return (
    <Reveal className="card-rotta p-7 bg-[#141414] flex flex-col">
      <div className="flex items-center gap-3 text-[#C6A762] mb-5">
        {icon}<span className="text-[11px] tracking-[0.3em] uppercase">{title}</span>
        <span className="ml-auto text-[10px] text-[#7d7d7d]">{items.length}</span>
      </div>
      <ul className="space-y-4">
        {items.map((it, i) => {
          const line = typeof it === "string" ? it : "";
          const url = showMap ? mapsSearchUrl(`${line} ${cityContext}`) : null;
          return (
            <li key={i} className="border-l border-[#2a2a2a] hover:border-[#C6A762]/60 transition-colors pl-4">
              <div className="text-white/95 text-sm leading-snug font-medium">{line}</div>
              {url && (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#C6A762] hover:text-white transition-colors"
                >
                  <MapPin size={11} /> Ver no mapa <ArrowRight size={11} />
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </Reveal>
  );
}

function EditorialGallery({ images, onOpen }) {
  const [hero, ...rest] = images;
  // Compact editorial layout: hero large on left, up to 4 thumbs in a 2x2 grid on right.
  const thumbs = rest.slice(0, 4);
  const extra = rest.slice(4);
  return (
    <div data-testid="city-gallery-editorial">
      <div className="grid gap-3 md:grid-cols-3 md:auto-rows-[200px]">
        <button
          onClick={() => onOpen(hero)}
          data-testid="gallery-hero"
          className="card-rotta img-zoom overflow-hidden md:col-span-2 md:row-span-2 h-[380px] md:h-auto"
        >
          <img src={hero} alt="" className="w-full h-full object-cover" loading="lazy" />
        </button>
        {thumbs.map((src, i) => (
          <button
            key={i}
            onClick={() => onOpen(src)}
            data-testid={`gallery-thumb-${i}`}
            className="card-rotta img-zoom overflow-hidden h-[190px] md:h-auto"
          >
            <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>
      {extra.length > 0 && (
        <div className="mt-3 grid gap-3 grid-cols-2 sm:grid-cols-4 lg:grid-cols-6">
          {extra.map((src, i) => (
            <button
              key={i}
              onClick={() => onOpen(src)}
              data-testid={`gallery-extra-${i}`}
              className="card-rotta img-zoom overflow-hidden aspect-square"
            >
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
